import { create } from 'zustand'

/**
 * Sprint 7 (Platform Command Center): the platform admin's active
 * impersonation session, if any.
 *
 * Deliberately a separate store from `store/platformAuth.js` (the admin's
 * own login) and `store/tenantAuth.js` (a real tenant user's own login) —
 * impersonating must never clobber either. The impersonation token
 * (`POST /platform/tickets/{id}/impersonate`'s `access_token`) is a bearer
 * JWT carrying an `impersonated_by` claim; `lib/api.js`'s `request()` attaches
 * it as an `Authorization: Bearer` header on every call while a session is
 * active, so the platform admin's browser starts acting as the impersonated
 * tenant user against every tenant-scoped/analytics endpoint, not just the
 * ticketing ones. Every such request is independently logged server-side by
 * `core.middleware.ImpersonationAuditMiddleware` — nothing client-side needs
 * to (or should) try to replicate that.
 *
 * Session-only: not persisted to localStorage. A page reload ends the
 * impersonation session, which is the safer default for a feature whose
 * whole point is a short-lived, auditable escalation.
 */

export const useImpersonation = create((set) => ({
  active: null, // {accessToken, tenantId, tenantName, impersonatedUserEmail, role, ticketId}

  start: (session) => set({ active: session }),
  end: () => set({ active: null }),
}))

/** Non-hook read for use outside components (the API client). */
export function getImpersonationToken() {
  return useImpersonation.getState().active?.accessToken ?? null
}

export function isImpersonating() {
  return Boolean(useImpersonation.getState().active)
}
